import Serializable from "@/core/misc/mixins/Serializable";
import Core         from "@/core/Core";
import DateSimple   from "@/core/types/DateSimple";
import Qty          from "@/core/types/Qty";

/**
 * Статус отгрузки позиции заказа (value object)
 *
 * Все величины — количества (Qty).
 */
class OrderPositionShipmentStatus extends Serializable {
    /** Сколько всего нужно отгрузить (дублирует orderPosition.qty) */
    public total: Qty;
    /** Принято от поставщика */
    public receivedFromSeller: Qty;
    /** Принято покупателем */
    public receivedByBuyer: Qty;
    /** Отгруженное количество */
    public shipped: Qty;
    /** Количество, готовое к отгрузке */
    public assembled: Qty;
    /** Количество неотгруженное/несобранное (в работе) */
    public inWork: Qty;
    /** Кол-во недостачи по приёмкам */
    public complainedByReceipt: Qty;
    /** Кол-во заявленного брака */
    public complainedSubstandard: Qty;
    /** Дата последней приёмки */
    public lastReceiptDate: DateSimple | null;

    /**
     * Данные прогресс-бара с сервера (сырые числа для отрисовки).
     *
     * Именно number, а не Qty: это ширины сегментов для вёрстки, а не
     * количества товара — арифметика семантических типов им не нужна, зато
     * нужен прямой доступ к числу. Раньше поле было объявлено как Qty, а
     * заполнялось Number() — тип врал.
     */
    public progressBar: { green: number; blue: number; purple: number; red: number; yellow: number };

    static fromServer(data: any, result = new OrderPositionShipmentStatus()): OrderPositionShipmentStatus {
        super.fromServer(data, result);

        // ---- Количества (super кладёт сырые строки — гидрируем в Qty) ----
        result.total                 = Core.utils.hydrate(data.total, Qty.fromServer)!;
        result.receivedFromSeller    = Core.utils.hydrate(data.receivedFromSeller, Qty.fromServer)!;
        result.receivedByBuyer       = Core.utils.hydrate(data.receivedByBuyer, Qty.fromServer)!;
        result.shipped               = Core.utils.hydrate(data.shipped, Qty.fromServer)!;
        result.assembled             = Core.utils.hydrate(data.assembled, Qty.fromServer)!;
        result.inWork                = Core.utils.hydrate(data.inWork, Qty.fromServer)!;
        result.complainedByReceipt   = Core.utils.hydrate(data.complainedByReceipt, Qty.fromServer)!;
        result.complainedSubstandard = Core.utils.hydrate(data.complainedSubstandard, Qty.fromServer)!;

        result.lastReceiptDate = Core.utils.hydrate(data.lastReceiptDate, DateSimple.fromServer);

        if (data.progressBar) {
            result.progressBar = {
                green:  Number(data.progressBar.green),
                blue:   Number(data.progressBar.blue),
                purple: Number(data.progressBar.purple),
                red:    Number(data.progressBar.red),
                yellow: Number(data.progressBar.yellow ?? 0),
            };
        } else {
            result.progressBar = OrderPositionShipmentStatus.emptyProgressBar();
        }

        return result;
    }

    /**
     * Создаёт статус отгрузки из позиции отгрузки
     */
    static fromShipmentPosition(shipmentPosition: any): OrderPositionShipmentStatus {
        const result = new OrderPositionShipmentStatus();

        OrderPositionShipmentStatus.fillEmpty(result);

        result.total = shipmentPosition.orderPosition.qty;
        result.receivedByBuyer = shipmentPosition.orderPosition.shipmentStatus.receivedByBuyer ?? Qty.zero();

        // Если есть сборка — берём оттуда, иначе из позиции заказа
        if (shipmentPosition.assemblyPosition) {
            result.shipped = shipmentPosition.assemblyPosition.qtyShipped;
        } else {
            result.shipped = shipmentPosition.orderPosition.shipmentStatus.shipped;
        }

        return result;
    }

    /**
     * Создаёт статус отгрузки из позиции заказа
     */
    static fromOrderPosition(orderPosition: any): OrderPositionShipmentStatus {
        const result = new OrderPositionShipmentStatus();

        OrderPositionShipmentStatus.fillEmpty(result);

        result.total = orderPosition.qty;
        result.receivedByBuyer = orderPosition.shipmentStatus.receivedByBuyer ?? Qty.zero();
        result.shipped = orderPosition.shipmentStatus.shipped ?? Qty.zero();

        return result;
    }

    /**
     * Забивает все количества нулями, дату — null, прогресс-бар — нулевым.
     *
     * Нужно потому, что fromShipmentPosition/fromOrderPosition заполняют лишь
     * три поля из девяти, а остальные оставались undefined. Любой шаблон,
     * дёрнувший у такого статуса assembled.format() или inWork.format(),
     * падал с «format is not a function» — той же ошибкой, что и сырые
     * строки с сервера.
     */
    private static fillEmpty(result: OrderPositionShipmentStatus): void {
        result.total                 = Qty.zero();
        result.receivedFromSeller    = Qty.zero();
        result.receivedByBuyer       = Qty.zero();
        result.shipped               = Qty.zero();
        result.assembled             = Qty.zero();
        result.inWork                = Qty.zero();
        result.complainedByReceipt   = Qty.zero();
        result.complainedSubstandard = Qty.zero();

        result.lastReceiptDate = null;
        result.progressBar     = OrderPositionShipmentStatus.emptyProgressBar();
    }

    private static emptyProgressBar(): { green: number; blue: number; purple: number; red: number; yellow: number } {
        return {
            green:  0,
            blue:   0,
            purple: 0,
            red:    0,
            yellow: 0,
        };
    }

    /**
     * Количество в пути (отгружено, но ещё не принято покупателем)
     */
    get inDelivery(): Qty {
        return this.shipped.minus(this.receivedByBuyer);
    }
}

export default OrderPositionShipmentStatus;